import { z } from "zod";

/**
 * The event contract of an interview session (decision 111, ENG-1014): everything that can be
 * written to a session's tape. The regional API appends each input to the tape with an
 * idempotency key and folds it with `apply` from `./session`; the island, the voice worker,
 * capture and the timers only ever send these events, so they are validated here, at the edge.
 *
 * Where events come from:
 * - **Person events** (`person.*`) from the island, always to the API over HTTP. Three more, the
 *   amendments (a note, a corrected answer, a removal), come from the package view once the
 *   recording has ended, through the API's own routes.
 * - **Heard segments, speaking state and say-intent outcomes** (`voice.*`, `say.outcome`) from the
 *   voice worker, which holds no interview state. `voice.status` and `mic.taken` are what the API
 *   and the desktop app see of the voice channel and the microphone.
 * - **Screen events** (`screen.*`) from capture.
 * - **A break** (`break`): the person has stopped talking and typing long enough for a natural
 *   pause, as the voice worker's turn detector or, with no voice, the island judged.
 * - **Timers** (`timer`): a wake-up from Hatchet. It carries nothing; the session works out from
 *   the entry's time whether a limit was reached, so a timer that fires early or twice is harmless.
 * - **Judgments** (`match.*`, `ask.*`): model-backed results computed elsewhere. Each names the
 *   tape position it was computed against (`basedOn`), so one that arrives after the question pool
 *   changed is discarded, and the input it judges (`input`), which must be the person's.
 *
 * Times are milliseconds since the epoch and come only from the tape entry, never from a clock,
 * so replaying a tape gives the same session.
 */

/** A question's ID, minted by whoever put it in the pool (a process question's own ID for those the draft carried in). */
export const questionIdSchema = z.string().min(1).max(64);
export type QuestionId = z.infer<typeof questionIdSchema>;

/** The tape position an event names: an input it judges, the state it was computed against, a receipt, an intent. */
const positionSchema = z.number().int().positive();

export const QUESTION_TEXT_MAX_CHARS = 280;
export const LIKELY_ANSWERS_MAX = 4;
export const LIKELY_ANSWER_MAX_CHARS = 80;
export const INPUT_TEXT_MAX_CHARS = 4_000;
export const NOTE_TEXT_MAX_CHARS = 280;

/** The longest name a not-shared span carries: an app's name, as a screen's context has it. */
export const UNSHARED_APP_MAX_CHARS = 120;

/**
 * The person moved into something that is not shared with Abacor (ENG-1031): an app outside the
 * shared set, one that is never seen, a private window or another display, named by `app`; or
 * back into what is shared (`app: null`). Capture keeps nothing while they are there. `own` marks
 * Abacor's own app (its chat, say), whose name is the development build's in development.
 */
export const screenUnsharedEventSchema = z.strictObject({
  type: z.literal("screen.unshared"),
  app: z.string().min(1).max(UNSHARED_APP_MAX_CHARS).nullable(),
  own: z.literal(true).optional(),
});

/** The person is in a password or card field (or left it): masked, not recorded. */
export const screenMaskedEventSchema = z.strictObject({
  type: z.literal("screen.masked"),
  masked: z.boolean(),
});

/**
 * Why capture kept a screen that is not something the person did, in capture's own words
 * (`CAPTURE_TRIGGERS` less `context`, a move to another app, window or page, which is).
 */
export const SCREEN_KEPT_FOR = ["click", "speech", "idle", "covered"] as const;

/** How long Abacor may still say a line after it was asked for; after that the worker drops it. */
export const SAY_INTENT_TTL_MS = 10_000;
/**
 * How much longer a line made while another is being said may wait for that one to end: about
 * the longest line Abacor says, the opening.
 */
export const SAY_WAIT_MS = 20_000;
/**
 * The opening (a greeting, how to take part, and "What are we looking at?") waits longer: it is
 * said as the recording starts, while the voice worker and the island are still joining the room.
 * It counts from when the session starts, or from Start when the person chooses what to share
 * first.
 */
export const OPENING_TTL_MS = 30_000;
/** A cut-in is about something on screen right now, so it goes stale sooner. */
export const CUT_IN_TTL_MS = 4_000;
/**
 * A question cut off this soon after it began was not heard: a word or two (3 November 2026, one
 * was cut off after 0.3 seconds and never asked again aloud). It is asked again at the next pause.
 */
export const CUT_SHORT_MS = 1_500;
/**
 * The person did something on screen this recently: they are still working, so a pause in what
 * they say is not a pause in the work, and a question that can wait waits (2 November 2026 demo
 * review: Abacor asked what they enter in the invoice while they were entering it).
 */
export const WORKING_MS = 5_000;
/**
 * The longest a pause is held for work that never pauses: past it, Abacor asks anyway, so a
 * question raised early is not pushed to the end of a recording where the screen is always busy
 * (2 November 2026, second recording: 81 screen actions in three and a half minutes held every
 * question until the person stopped).
 */
export const MAX_HOLD_MS = 30_000;
/**
 * A break fills the pool from what the process still needs only once the person has done nothing
 * on screen for this long: a pause in the work, not a breath between sentences. Replayed on the
 * two 2 November 2026 recordings (`apps/api/src/modules/recordings/eval/replay-run.ts`), filling
 * at every break asked how an entry is checked before any entry was shown; waiting for 20 seconds
 * of stillness halved the questions left open at the end and the ones asked just before the person
 * showed the answer (exploration/research/interview-timing-2026-11).
 */
export const WORK_PAUSED_MS = 20_000;

/**
 * When Abacor asks what can wait, chosen at Start so it can be tried on real recordings (2 November
 * 2026; the rules are compared in exploration/research/interview-timing-2026-11):
 * - `work-pause`: a question is held while the person works on screen, and the pool is filled only
 *   once the screen has been still for `WORK_PAUSED_MS`. What a Start that names no mode means
 *   (`DEFAULT_ASKING`), so tapes from before the choice replay as they were.
 * - `speech-pause`: as before 2 November, the pool is filled at every pause in speech.
 * - `step-end`, what the island's Start names unless a development build chose otherwise
 *   (`START_ASKING`), judged the best balance once the three were tried: Abacor asks at the first
 *   pause within `STEP_ASK_WITHIN_MS` of the person reaching a page they had not been on for
 *   `STEP_NEW_PAGE_MS`, the end of a step, or after `STEP_HOLD_MS`.
 */
export const ASKING_MODES = ["work-pause", "speech-pause", "step-end"] as const;
export const askingModeSchema = z.enum(ASKING_MODES);
export type AskingMode = z.infer<typeof askingModeSchema>;
export const DEFAULT_ASKING: AskingMode = "work-pause";
export const START_ASKING: AskingMode = "step-end";
/** A page not visited for this long is a new one: reaching it ends a step (`step-end`). */
export const STEP_NEW_PAGE_MS = 60_000;
/** How soon after a step ends a pause may ask (`step-end`). */
export const STEP_ASK_WITHIN_MS = 15_000;
/** The longest a question waits for a step to end (`step-end`). */
export const STEP_HOLD_MS = 45_000;
/** Paused this long, Abacor asks whether to finish. */
export const PAUSED_PROMPT_MS = 15 * 60_000;
/** Recorded this long, Abacor suggests wrapping this part up. */
export const NUDGE_MS = 30 * 60_000;
/** Recorded this long, the recording stops; the next part is a new recording. */
export const STOP_MS = 60 * 60_000;

/**
 * `now`: about something on screen that will not come back, the only kind Abacor may cut in with.
 * `later`: waits for a natural pause.
 */
export const urgencySchema = z.enum(["now", "later"]);
export type Urgency = z.infer<typeof urgencySchema>;

/** Where a question came from: the chat or draft before the recording, or what was seen or said in it. */
export const questionOriginSchema = z.enum(["chat", "screen", "speech"]);

/**
 * One thing a process definition needs (a case unit, a step's Done when, a table's rows), named
 * by the API's list of needs, which reads them from the processes contract. The session never
 * interprets it: it only keeps which notes and questions cover which need.
 */
export const needIdSchema = z
  .string()
  .max(64)
  .regex(/^(process|step|table)\.[a-z][A-Za-z]*$/);
export type NeedId = z.infer<typeof needIdSchema>;

/**
 * A need covered (or aimed at), and which step or table it is about in the person's words, or
 * none for a need of the whole process. With a draft, `about` is the step's or table's own ID.
 */
export const coverSchema = z.strictObject({
  need: needIdSchema,
  about: z.string().trim().min(1).max(80).optional(),
});
export type Cover = z.infer<typeof coverSchema>;

/** A note covers at most this many needs. */
export const NOTE_COVERS_MAX = 3;

/** A question as it enters the pool, before the session gives it a state. */
export const questionSeedSchema = z.strictObject({
  id: questionIdSchema,
  text: z.string().min(1).max(QUESTION_TEXT_MAX_CHARS),
  urgency: urgencySchema,
  origin: questionOriginSchema,
  /** What prompted it, shown with the question ("PO 4471 has 14 lines"). */
  evidence: z.string().max(QUESTION_TEXT_MAX_CHARS),
  /** Tap-to-answer options. */
  likely: z.array(z.string().min(1).max(LIKELY_ANSWER_MAX_CHARS)).max(LIKELY_ANSWERS_MAX),
  /** The need it was raised to close; answered or closed, the question covers it. Absent on older tapes. */
  need: coverSchema.optional(),
});
export type QuestionSeed = z.infer<typeof questionSeedSchema>;

/** What Abacor takes from an input for the rough understanding: a short topic and one line. */
export const noteSchema = z.strictObject({
  topic: z.string().min(1).max(80),
  text: z.string().min(1).max(NOTE_TEXT_MAX_CHARS),
  /** The needs it covers, as the match judgment guessed them. Absent on older tapes. */
  covers: z.array(coverSchema).max(NOTE_COVERS_MAX).optional(),
});

const inputText = z.string().trim().min(1).max(INPUT_TEXT_MAX_CHARS);

/** The longest reply Abacor writes in its own words (a recall, or what a question is after). */
export const REPLY_TEXT_MAX_CHARS = 240;

/**
 * What an input asks of Abacor itself, when it speaks to Abacor rather than about the work: the
 * match judgment says so beside what the input answers or teaches. Abacor replies once, short, and
 * never with anything that counts as an answer.
 *
 * - `aside`: meant for someone else in the room. It teaches nothing and gets no reply.
 * - `status`: about the session itself (can it see, can it hear, what happens next), answered
 *   from what the session knows, not from what a model wrote.
 * - `recall`: what the person said or Abacor noted ("did I cover the fuel surcharge?"), answered in
 *   `answer` from the notes and answers alone.
 * - `judge`: asks Abacor to judge the work or say how it should be done. Abacor does not, kindly;
 *   the person's doubt (`doubt`) is kept as a note for the chat by its own entry (`match.doubt`).
 * - `recap`: what Abacor has got so far: a line, and its view opens.
 * - `repeat`: say the last thing again, as it was.
 * - `hold`: give the person a moment; no questions until they carry on.
 * - `clarify`: what the question showing means: `meaning`, what it is after, never an answer.
 *
 * Putting the question showing off ("skip that", "later") is the match's `declined`.
 */
export const requestSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("aside") }),
  z.strictObject({
    kind: z.literal("status"),
    about: z.enum(["screen", "hearing", "next"]),
  }),
  z.strictObject({
    kind: z.literal("recall"),
    answer: z.string().trim().min(1).max(REPLY_TEXT_MAX_CHARS),
  }),
  z.strictObject({
    kind: z.literal("judge"),
    doubt: z.string().trim().min(1).max(NOTE_TEXT_MAX_CHARS),
  }),
  z.strictObject({ kind: z.literal("recap") }),
  z.strictObject({ kind: z.literal("repeat") }),
  z.strictObject({ kind: z.literal("hold") }),
  z.strictObject({
    kind: z.literal("clarify"),
    meaning: z.string().trim().min(1).max(REPLY_TEXT_MAX_CHARS),
  }),
]);
export type Request = z.infer<typeof requestSchema>;
/** What Abacor takes from an answer: the receipt's title and the line in the rough understanding. */
const took = z.string().min(1).max(NOTE_TEXT_MAX_CHARS);

const judged = { input: positionSchema, basedOn: positionSchema };

/** What the person does on the island. The island sends only these, always to the API. */
const personEvents = [
  /** Typed text. `questionId` when it was typed into a question's own field (Type instead), which answers it. */
  z.strictObject({
    type: z.literal("person.typed"),
    text: inputText,
    questionId: questionIdSchema.optional(),
  }),
  /** A tapped likely answer, by its index in the question's `likely`. */
  z.strictObject({
    type: z.literal("person.tapped"),
    questionId: questionIdSchema,
    option: z.number().int().nonnegative(),
  }),
  /** The typing field opened or closed. Typing silences Abacor. */
  z.strictObject({ type: z.literal("person.typing"), open: z.boolean() }),
  /** Not now: the question showing backs off, or the clarifier is dismissed. */
  z.strictObject({ type: z.literal("person.notNow") }),
  /** The person picked which question the clarifier's input was about. */
  z.strictObject({
    type: z.literal("person.chose"),
    questionId: questionIdSchema,
  }),
  /** The person picked a question from the open list; Abacor asks it. */
  z.strictObject({
    type: z.literal("person.picked"),
    questionId: questionIdSchema,
  }),
  /** Undo or Reopen on a receipt, named by the tape position that made it. */
  z.strictObject({ type: z.literal("person.undo"), receipt: positionSchema }),
  /** What I've got, the open list, or neither. */
  z.strictObject({
    type: z.literal("person.view"),
    view: z.enum(["understanding", "open"]).nullable(),
  }),
  /**
   * Start, once the person chose what Abacor sees (ENG-1031): recorded time counts from here, not
   * from when the recording was made.
   */
  z.strictObject({
    type: z.literal("person.start"),
    /** How Abacor asks in this recording; `DEFAULT_ASKING` when absent. */
    asking: askingModeSchema.optional(),
  }),
  z.strictObject({ type: z.literal("person.pause") }),
  z.strictObject({ type: z.literal("person.resume") }),
  z.strictObject({ type: z.literal("person.finish") }),
  /** Answer now, from the finish summary: back into the session with the questions it held. */
  z.strictObject({ type: z.literal("person.answerNow") }),
  /** Mic off makes a typing-only session. */
  z.strictObject({ type: z.literal("person.mic"), on: z.boolean() }),
  /** Abacor's voice muted: captions only. Its lines are still written. */
  z.strictObject({ type: z.literal("person.voice"), muted: z.boolean() }),
] as const;

export const personEventSchema = z.discriminatedUnion("type", [...personEvents]);
export type PersonEvent = z.infer<typeof personEventSchema>;

/** A moment on the recording's clock, in epoch milliseconds, as the package view picked it. */
const momentSchema = z.number().int().nonnegative();

/** A screen's or a trace line's ID, as the API keeps it. */
const partIdSchema = z.string().min(1).max(64);

/**
 * One part of an ended recording the person takes out (ENG-1029, D3), or every part in a stretch
 * of its clock. A screen or an action names the tape entry it put there (`entry`), which the API
 * finds by the part's own key; null when it put none (one that came while paused, or an action no
 * judgment reads). A line said or typed, or a note added later, is named by its own entry. A
 * stretch names the entries of the screens and actions captured in it (`entries`), found by the
 * time they were captured: their entries landed when they were delivered, which can be later.
 */
/** The most captured parts one stretch's removal carries; a longer one is refused, not cut. */
export const REMOVED_RANGE_ENTRIES_MAX = 5_000;

export const removedPartSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("screen"),
    id: partIdSchema,
    entry: positionSchema.nullable(),
  }),
  z.strictObject({
    kind: z.literal("action"),
    id: partIdSchema,
    entry: positionSchema.nullable(),
  }),
  z.strictObject({ kind: z.literal("said"), seq: positionSchema }),
  z
    .strictObject({
      kind: z.literal("range"),
      from: momentSchema,
      to: momentSchema,
      entries: z.array(positionSchema).max(REMOVED_RANGE_ENTRIES_MAX),
    })
    .refine(({ from, to }) => to > from, "A stretch ends after it starts"),
]);
export type RemovedPart = z.infer<typeof removedPartSchema>;

/**
 * What the person changes in a recording after it ended (ENG-1029, decisions D1 and D3), from the
 * package view. Nothing captured is edited: each is added to the tape, and the session folds it.
 * The island never sends these; the API appends them from its own routes, only for the chat's
 * owner who made the recording, and only once it has ended.
 */
const amendmentEvents = [
  /** A note at a moment: the person's own words, evidence like anything said while recording. */
  z.strictObject({
    type: z.literal("person.note"),
    moment: momentSchema,
    text: inputText,
  }),
  /**
   * A question's answer, given again at `moment`: the earlier answer stays, linked, and this one
   * counts. A question with no answer that stands is answered by it. `replaces` is the answer the
   * person saw standing (its receipt), or null for none: when another stands by the time this
   * lands, it is dropped as `stale`, so an editor left open never overrides a decision it did not
   * see.
   */
  z.strictObject({
    type: z.literal("person.corrected"),
    questionId: questionIdSchema,
    moment: momentSchema,
    text: inputText,
    replaces: positionSchema.nullable(),
  }),
  /** A part, or a stretch of time, taken out: logged, and what Abacor took from it dropped. */
  z.strictObject({
    type: z.literal("person.removed"),
    part: removedPartSchema,
  }),
] as const;

export const amendmentEventSchema = z.discriminatedUnion("type", [...amendmentEvents]);
export type AmendmentEvent = z.infer<typeof amendmentEventSchema>;

/**
 * A natural pause: the person has stopped talking and typing long enough. The voice worker's turn
 * detector sends it, or with no voice the island does.
 */
const breakEvent = z.strictObject({ type: z.literal("break") });

/** What the voice worker sends: what it heard, whether the person is speaking, and what became of a say intent. */
const voiceEvents = [
  /** A final segment of the person's speech, as the voice worker heard it. */
  z.strictObject({
    type: z.literal("voice.heard"),
    text: inputText,
    startMs: z.number().nonnegative(),
    endMs: z.number().nonnegative(),
  }),
  /** Whether the person is speaking right now (voice activity). */
  z.strictObject({ type: z.literal("voice.speaking"), speaking: z.boolean() }),
  /** Whether the voice channel is up; `offline` when the worker or the network dropped. */
  z.strictObject({
    type: z.literal("voice.status"),
    status: z.enum(["live", "offline"]),
  }),
  /**
   * What happened to a say intent, named by the tape position that made it. `cut-off` is a
   * barge-in: the worker stopped speaking the moment the person talked and reports it afterward.
   */
  z.strictObject({
    type: z.literal("say.outcome"),
    intent: positionSchema,
    outcome: z.enum(["started", "finished", "cut-off"]),
    /**
     * With `cut-off` after `started`: how long it played before the person cut in, as the worker
     * measured it (the API's arrival times move with retries). A worker before it sends none.
     */
    heardMs: z.number().int().nonnegative().optional(),
  }),
  breakEvent,
] as const;

/** The events the voice worker may send; the Voice API checks each against this and nothing else. */
export const voiceEventSchema = z.discriminatedUnion("type", [...voiceEvents]);
export type VoiceEvent = z.infer<typeof voiceEventSchema>;

export const sessionEventSchema = z.discriminatedUnion("type", [
  /**
   * The first entry on every tape. `questions` are the draft's open process questions carried in
   * from the chat. With no `intent`, Abacor asks what this is. `voice` is `none` for a session that
   * asks in text only (the first release), `live` when a voice worker is in the room. `mic` is
   * `off` for a session whose person turns the mic on themselves (voice starts with it off, for
   * privacy); without it the mic starts on, as tapes before it did.
   */
  z.strictObject({
    type: z.literal("session.started"),
    intent: z.string().trim().min(1).max(200).nullable(),
    voice: z.enum(["none", "live"]),
    mic: z.enum(["on", "off"]).optional(),
    questions: z.array(questionSeedSchema).max(50),
  }),

  ...personEvents,
  ...amendmentEvents,

  ...voiceEvents,
  /** A call took the microphone, or gave it back. */
  z.strictObject({ type: z.literal("mic.taken"), taken: z.boolean() }),
  /**
   * Something the person did on screen, as capture describes it (never typed content). `kept` is
   * set when the screen was kept for a moment rather than for something they did (just before a
   * click, as they started talking, after a long pause, at a covered field): a picture of where
   * they were for the package, which is never judged.
   */
  z.strictObject({
    type: z.literal("screen.action"),
    text: z.string().min(1).max(280),
    keyframe: z.string().min(1).max(64).optional(),
    kept: z.enum(SCREEN_KEPT_FOR).optional(),
  }),
  screenMaskedEventSchema,
  screenUnsharedEventSchema,

  z.strictObject({ type: z.literal("timer") }),

  /** The input fits exactly one question in the pool; `request` is what it also asks of Abacor. */
  z.strictObject({
    type: z.literal("match.one"),
    ...judged,
    questionId: questionIdSchema,
    took,
    request: requestSchema.optional(),
  }),
  /** The input fits two questions: Abacor asks which with `clarifier`. */
  z.strictObject({
    type: z.literal("match.two"),
    ...judged,
    fits: z
      .array(z.strictObject({ questionId: questionIdSchema, took }))
      .length(2)
      .refine(([a, b]) => a?.questionId !== b?.questionId, "Two different questions"),
    clarifier: z.string().min(1).max(QUESTION_TEXT_MAX_CHARS),
  }),
  /**
   * The input fits no question: it is teaching. `note` is what Abacor takes from it, if anything;
   * `corrects` names an earlier note (by the position that made it) the person has just corrected;
   * `declined` says the person said "not now" to the question showing. `request` is what the input
   * asks of Abacor itself.
   */
  z.strictObject({
    type: z.literal("match.none"),
    ...judged,
    note: noteSchema.optional(),
    corrects: positionSchema.optional(),
    declined: z.literal(true).optional(),
    request: requestSchema.optional(),
  }),
  /**
   * What the person is unsure of, when they asked Abacor to judge their work: kept as a note for
   * the chat. Its own entry, since a note is known by the entry that made it and the same input
   * may have taught one too.
   */
  z.strictObject({
    type: z.literal("match.doubt"),
    ...judged,
    doubt: z.string().trim().min(1).max(NOTE_TEXT_MAX_CHARS),
  }),
  /** The person's words say what the recording is about, when it started without an intent. */
  z.strictObject({
    type: z.literal("match.intent"),
    ...judged,
    intent: z.string().min(1).max(200),
  }),
  /** A new question for the pool, from what was seen or said. */
  z.strictObject({
    type: z.literal("ask.raised"),
    basedOn: positionSchema,
    question: questionSeedSchema,
  }),
  /** Now is the moment for an "about now" question: Abacor cuts in, after a cue. */
  z.strictObject({
    type: z.literal("ask.cutIn"),
    basedOn: positionSchema,
    questionId: questionIdSchema,
  }),
]);
export type SessionEvent = z.infer<typeof sessionEventSchema>;
export type SessionEventType = SessionEvent["type"];

/**
 * One entry on the tape. `seq` is its position, from 1, with no gaps; `key` is the sender's
 * idempotency key (a client event ID, a segment ID, a judgment's request ID); `at` is when the API
 * appended it.
 */
export const tapeEntrySchema = z.strictObject({
  seq: positionSchema,
  key: z.string().min(1).max(128),
  at: z.number().int().nonnegative(),
  event: sessionEventSchema,
});
export type TapeEntry = z.infer<typeof tapeEntrySchema>;
